// assets/predictive-search.js - Enhanced v2.0
// Modern predictive search with improved UI

(function () {
  'use strict';

  class EnhancedPredictiveSearch {
    constructor() {
      this.searchInputs = [];
      this.activeResultsContainer = null;
      this.debounceTimeout = null;
      this.cache = new Map();
      this.recentSearches = this.getRecentSearches();
      this.init();
    }

    init() {
      // Find all search inputs
      this.searchInputs = document.querySelectorAll('.search-input');

      // Initialized

      if (this.searchInputs.length === 0) {
        console.warn('⚠️ No search inputs found with class .search-input');
        return;
      }

      this.searchInputs.forEach((input, index) => {
        // Setup input
        this.setupSearchInput(input);
      });

      // Close on click outside
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.search-input') && !e.target.closest('.predictive-search-dropdown')) {
          this.closeAllResults();
        }
      });

      // Close on Escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeAllResults();
        }
      });

      // Initialized
    }

    setupSearchInput(input) {
      // Create results container
      const container = document.createElement('div');
      container.className = 'predictive-search-dropdown';
      container.setAttribute('role', 'listbox');
      container.innerHTML = this.getEmptyStateHTML();

      // Insert after input wrapper
      const wrapper = input.closest('.search-input-wrapper') || input.parentNode;
      wrapper.style.position = 'relative';
      wrapper.appendChild(container);

      // Event listeners
      input.addEventListener('input', (e) => this.handleInput(e.target, container));
      input.addEventListener('focus', (e) => this.handleFocus(e.target, container));
      input.addEventListener('keydown', (e) => this.handleKeydown(e, container));
    }

    handleInput(input, container) {
      clearTimeout(this.debounceTimeout);
      const query = input.value.trim();


      if (query.length < 2) {
        container.innerHTML = this.getRecentSearchesHTML();
        container.classList.add('active');
        return;
      }

      // Show loading
      container.innerHTML = this.getLoadingHTML();
      container.classList.add('active');


      this.debounceTimeout = setTimeout(() => {
        this.performSearch(query, container);
      }, 250);
    }

    handleFocus(input, container) {
      this.activeResultsContainer = container;

      if (input.value.trim().length < 2) {
        container.innerHTML = this.getRecentSearchesHTML();
      }

      container.classList.add('active');
    }

    handleKeydown(e, container) {
      const items = container.querySelectorAll('.search-result-item');
      if (items.length === 0) return;

      const current = container.querySelector('.search-result-item.active');
      let index = Array.from(items).indexOf(current);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          index = (index + 1) % items.length;
          break;
        case 'ArrowUp':
          e.preventDefault();
          index = index <= 0 ? items.length - 1 : index - 1;
          break;
        case 'Enter':
          if (current && current.href) {
            e.preventDefault();
            this.saveRecentSearch(e.target.value);
            window.location.href = current.href;
          }
          return;
        default:
          return;
      }

      items.forEach(item => item.classList.remove('active'));
      if (items[index]) {
        items[index].classList.add('active');
        items[index].scrollIntoView({ block: 'nearest' });
      }
    }

    async performSearch(query, container) {

      // Check cache
      if (this.cache.has(query)) {
        this.displayResults(this.cache.get(query), query, container);
        return;
      }

      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,article&resources[limit]=6`;

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error('Search failed');
        }

        const data = await response.json();

        this.cache.set(query, data);
        this.displayResults(data, query, container);

      } catch (error) {
        container.innerHTML = this.getErrorHTML();
      }
    }

    displayResults(data, query, container) {

      if (!data.resources || !data.resources.results) {
        container.innerHTML = this.getNoResultsHTML(query);
        return;
      }

      const { products = [], collections = [], articles = [] } = data.resources.results;


      if (products.length === 0 && collections.length === 0 && articles.length === 0) {
        container.innerHTML = this.getNoResultsHTML(query);
      } else {
        const html = this.buildResultsHTML(products, collections, articles, query);
        container.innerHTML = html;
      }

      container.classList.add('active');
    }

    buildResultsHTML(products, collections, articles, query) {
      let html = '';

      // Products Section
      if (products.length > 0) {
        html += `
          <div class="search-section">
            <div class="search-section-header">
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
              </svg>
              Productos
              <span class="search-section-count">${products.length}</span>
            </div>
            <div class="search-products-grid">
              ${products.map(product => this.buildProductHTML(product)).join('')}
            </div>
          </div>
        `;
      }

      // Collections Section
      if (collections.length > 0) {
        html += `
          <div class="search-section">
            <div class="search-section-header">
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
              </svg>
              Categorías
            </div>
            ${collections.map(collection => this.buildCollectionHTML(collection)).join('')}
          </div>
        `;
      }

      // Articles Section
      if (articles.length > 0) {
        html += `
          <div class="search-section">
            <div class="search-section-header">
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
              </svg>
              Artículos
            </div>
            ${articles.map(article => this.buildArticleHTML(article)).join('')}
          </div>
        `;
      }

      // View All Button
      html += `
        <div class="search-view-all">
          <a href="/search?q=${encodeURIComponent(query)}" class="search-view-all-btn" onclick="predictiveSearch.saveRecentSearch('${query.replace(/'/g, "\\'")}')">
            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            Ver todos los resultados para "${query}"
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
            </svg>
          </a>
        </div>
      `;

      return html;
    }

    buildProductHTML(product) {
      const price = product.price ? this.formatPrice(product.price) : '';
      const comparePrice = product.compare_at_price_max && product.compare_at_price_max > product.price
        ? this.formatPrice(product.compare_at_price_max)
        : '';
      const image = product.image || 'https://cdn.shopify.com/s/files/1/0555/2789/9193/files/placeholder.jpg';

      return `
        <a href="${product.url}" class="search-result-item search-product-item">
          <div class="search-product-image">
            <img src="${image}" alt="${product.title}" loading="lazy">
          </div>
          <div class="search-product-info">
            <h4 class="search-product-title">${product.title}</h4>
            <div class="search-product-price">
              <span class="price-current">${price}</span>
              ${comparePrice ? `<span class="price-compare">${comparePrice}</span>` : ''}
            </div>
          </div>
        </a>
      `;
    }

    buildCollectionHTML(collection) {
      return `
        <a href="${collection.url}" class="search-result-item search-collection-item">
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
          </svg>
          <span>${collection.title}</span>
          <svg class="arrow" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </a>
      `;
    }

    buildArticleHTML(article) {
      return `
        <a href="${article.url}" class="search-result-item search-article-item">
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          <span>${article.title}</span>
        </a>
      `;
    }

    formatPrice(price) {
      return new Intl.NumberFormat('es-PY', {
        style: 'currency',
        currency: 'PYG',
        minimumFractionDigits: 0
      }).format(price);
    }

    getLoadingHTML() {
      return `
        <div class="search-loading">
          <div class="search-loading-spinner"></div>
          <span>Buscando...</span>
        </div>
      `;
    }

    getNoResultsHTML(query) {
      return `
        <div class="search-no-results">
          <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <h4>No encontramos resultados para "${query}"</h4>
          <p>Intenta con otros términos o explora nuestras categorías</p>
        </div>
      `;
    }

    getRecentSearchesHTML() {
      if (this.recentSearches.length === 0) {
        return this.getEmptyStateHTML();
      }

      return `
        <div class="search-recent">
          <div class="search-section-header">
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            Búsquedas Recientes
            <button class="search-clear-recent" onclick="predictiveSearch.clearRecentSearches()">Limpiar</button>
          </div>
          ${this.recentSearches.map(term => `
            <a href="/search?q=${encodeURIComponent(term)}" class="search-result-item search-recent-item">
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <span>${term}</span>
            </a>
          `).join('')}
        </div>
      `;
    }

    getEmptyStateHTML() {
      return `
        <div class="search-empty-state">
          <div class="search-popular">
            <div class="search-section-header">
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
              </svg>
              Búsquedas Populares
            </div>
            <div class="search-popular-tags">
              <a href="/search?q=notebook" class="search-tag">Notebook</a>
              <a href="/search?q=celular" class="search-tag">Celular</a>
              <a href="/search?q=monitor" class="search-tag">Monitor</a>
              <a href="/search?q=auriculares" class="search-tag">Auriculares</a>
              <a href="/search?q=tablet" class="search-tag">Tablet</a>
            </div>
          </div>
        </div>
      `;
    }

    getErrorHTML() {
      return `
        <div class="search-error">
          <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
          <h4>Error al buscar</h4>
          <p>Por favor intenta de nuevo</p>
        </div>
      `;
    }

    closeAllResults() {
      document.querySelectorAll('.predictive-search-dropdown').forEach(container => {
        container.classList.remove('active');
      });
    }

    getRecentSearches() {
      try {
        return JSON.parse(localStorage.getItem('recentSearches') || '[]');
      } catch {
        return [];
      }
    }

    saveRecentSearch(term) {
      if (!term || term.length < 2) return;

      let searches = this.getRecentSearches();
      searches = searches.filter(s => s !== term);
      searches.unshift(term);
      searches = searches.slice(0, 5);

      localStorage.setItem('recentSearches', JSON.stringify(searches));
      this.recentSearches = searches;
    }

    clearRecentSearches() {
      localStorage.removeItem('recentSearches');
      this.recentSearches = [];
      document.querySelectorAll('.predictive-search-dropdown.active').forEach(container => {
        container.innerHTML = this.getEmptyStateHTML();
      });
    }
  }

  // Initialize
  document.addEventListener('DOMContentLoaded', () => {
    window.predictiveSearch = new EnhancedPredictiveSearch();
  });

  // CSS Styles
  const style = document.createElement('style');
  style.textContent = `
    .predictive-search-dropdown {
      position: absolute;
      top: calc(100% + 8px);
      left: 0;
      right: 0;
      background: white;
      border-radius: 16px;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
      border: 1px solid #e5e7eb;
      z-index: 1000;
      max-height: 480px;
      overflow-y: auto;
      opacity: 0;
      visibility: hidden;
      transform: translateY(-8px);
      transition: all 0.2s ease;
    }
    
    .predictive-search-dropdown.active {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }
    
    .search-section {
      border-bottom: 1px solid #f3f4f6;
    }
    
    .search-section:last-of-type {
      border-bottom: none;
    }
    
    .search-section-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 16px;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #6b7280;
      background: #f9fafb;
    }
    
    .search-section-count {
      margin-left: auto;
      background: #0055A4;
      color: white;
      padding: 2px 8px;
      border-radius: 10px;
      font-size: 11px;
    }
    
    .search-clear-recent {
      margin-left: auto;
      background: none;
      border: none;
      color: #0055A4;
      font-size: 12px;
      cursor: pointer;
      text-transform: none;
      letter-spacing: 0;
    }
    
    .search-clear-recent:hover {
      text-decoration: underline;
    }
    
    .search-products-grid {
      display: flex;
      flex-direction: column;
      gap: 0;
      background: #fff;
    }
    
    .search-result-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      color: #374151;
      text-decoration: none;
      transition: all 0.15s ease;
      background: white;
      border-bottom: 1px solid #f3f4f6;
    }
    
    .search-result-item:hover,
    .search-result-item.active {
      background: #eff6ff;
      color: #0055A4;
    }
    
    .search-product-item {
      flex-direction: row;
      align-items: center;
      padding: 12px 16px;
    }
    
    .search-product-image {
      width: 80px;
      height: 80px;
      flex-shrink: 0;
      border-radius: 8px;
      overflow: hidden;
      background: linear-gradient(135deg, #f3f4f6, #e5e7eb);
    }
    
    .search-product-image img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;
    }
    
    .search-product-item:hover .search-product-image img {
      transform: scale(1.05);
    }
    
    .search-product-info {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    
    .search-product-title {
      font-size: 14px;
      font-weight: 600;
      color: #111827;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      line-height: 1.3;
    }
    
    .search-product-price {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: nowrap;
    }
    
    .price-current {
      font-size: 15px;
      font-weight: 700;
      color: #0055A4;
      white-space: nowrap;
    }
    
    .price-compare {
      font-size: 12px;
      color: #9ca3af;
      text-decoration: line-through;
      white-space: nowrap;
    }
    
    .search-collection-item,
    .search-article-item,
    .search-recent-item {
      gap: 10px;
    }
    
    .search-collection-item svg,
    .search-article-item svg,
    .search-recent-item svg {
      color: #9ca3af;
      flex-shrink: 0;
    }
    
    .search-collection-item span,
    .search-article-item span,
    .search-recent-item span {
      flex: 1;
    }
    
    .search-collection-item .arrow {
      opacity: 0;
      transform: translateX(-5px);
      transition: all 0.2s ease;
    }
    
    .search-collection-item:hover .arrow {
      opacity: 1;
      transform: translateX(0);
    }
    
    .search-view-all {
      padding: 12px 16px;
      background: #f9fafb;
      border-top: 1px solid #e5e7eb;
    }
    
    .search-view-all-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      padding: 14px 20px;
      background: linear-gradient(135deg, #0055A4, #003d7a);
      color: white;
      font-weight: 600;
      font-size: 14px;
      border-radius: 12px;
      text-decoration: none;
      transition: all 0.2s ease;
    }
    
    .search-view-all-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(0, 85, 164, 0.3);
    }
    
    .search-loading {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px;
      gap: 16px;
      color: #6b7280;
    }
    
    .search-loading-spinner {
      width: 32px;
      height: 32px;
      border: 3px solid #e5e7eb;
      border-top-color: #0055A4;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
    
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    
    .search-no-results,
    .search-error {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px 20px;
      text-align: center;
      color: #6b7280;
    }
    
    .search-no-results svg,
    .search-error svg {
      color: #d1d5db;
      margin-bottom: 16px;
    }
    
    .search-no-results h4,
    .search-error h4 {
      font-size: 16px;
      font-weight: 600;
      color: #374151;
      margin-bottom: 8px;
    }
    
    .search-no-results p,
    .search-error p {
      font-size: 14px;
    }
    
    .search-popular-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      padding: 12px 16px;
    }
    
    .search-tag {
      padding: 8px 16px;
      background: #f3f4f6;
      color: #374151;
      font-size: 13px;
      font-weight: 500;
      border-radius: 20px;
      text-decoration: none;
      transition: all 0.2s ease;
    }
    
    .search-tag:hover {
      background: #0055A4;
      color: white;
    }
  `;
  document.head.appendChild(style);
})();
